import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "../../dashboard/Skeleton";
import {
  type SupportMessage,
  type SupportMessageResult,
  type SupportMessageSummary,
  useSupportMessage,
} from "../../../hooks/useAdminSupportMessages";
import { formatDateTime, formatRelativeTime } from "../../../utils/formatDateTime";
import styles from "../../../pages/AdminInstances.module.css";
import own from "./SupportMessages.module.css";

type Props = {
  readonly id: string;
  // The table row when it is on screen; a deep link opens before the list loads.
  readonly summary: SupportMessageSummary | null;
  readonly onClose: () => void;
  readonly onReview: (id: string) => Promise<SupportMessageResult>;
};

export const agentLabel = (m: SupportMessageSummary): string => m.agentName ?? m.tenantName ?? m.tenantId;

// Native <dialog> pinned to the right edge: the browser traps focus and closes it on Escape.
// The body is agent-written and may carry injected content, so it renders as plain text.
export const SupportMessagePanel = ({ id, summary, onClose, onReview }: Props): JSX.Element => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { status, message, error, replace } = useSupportMessage(id);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState<boolean>(false);
  const shown: SupportMessageSummary | SupportMessage | null = message ?? summary;
  // close() rather than unmounting directly, so the browser returns focus to the row.
  const close = (): void => dialogRef.current?.close();

  useEffect(() => {
    if (!dialogRef.current?.open) dialogRef.current?.showModal();
  }, []);

  const review = async (): Promise<void> => {
    setReviewing(true);
    setReviewError(null);
    const result = await onReview(id);
    if (result.ok) replace(result.value);
    else setReviewError(result.message);
    setReviewing(false);
  };

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the click only catches the backdrop; Escape closes a modal dialog natively.
    <dialog
      ref={dialogRef}
      className={own.panel}
      aria-labelledby="support-message-subject"
      data-testid="support-message-panel"
      onClose={onClose}
      onClick={(e) => {
        if (e.target === dialogRef.current) close();
      }}
    >
      <div className={own.panelContent}>
        <header className={own.panelHeader}>
          <h3 id="support-message-subject" className={own.subject}>
            {shown?.subject ?? <Skeleton width="60%" height="1rem" />}
          </h3>
          <button type="button" className={styles.secondaryBtn} onClick={close} aria-label="Close">
            ✕
          </button>
        </header>

        {shown !== null && <MessageFacts message={shown} />}
        {shown !== null && (
          <ReviewBar message={shown} busy={reviewing} error={reviewError} onReview={() => void review()} />
        )}

        {status === "loading" && <Skeleton width="100%" height="12rem" />}
        {status === "error" && <p role="alert">{error ?? "Failed to load the message."}</p>}
        {message !== null && <pre className={own.body}>{message.body}</pre>}
      </div>
    </dialog>
  );
};

const MessageFacts = ({ message: shown }: { readonly message: SupportMessageSummary }): JSX.Element => (
  <dl className={own.facts}>
    <dt>Agent</dt>
    <dd>
      {agentLabel(shown)}{" "}
      <Link to={`/dashboard/admin/instances/${encodeURIComponent(shown.tenantId)}`} className={own.muted}>
        {shown.tenantName ?? shown.tenantId}
      </Link>
    </dd>
    <dt>Sent</dt>
    <dd title={formatDateTime(shown.createdAt)}>
      {formatDateTime(shown.createdAt)} · {formatRelativeTime(shown.createdAt)}
    </dd>
    <dt>Type</dt>
    <dd>
      <span className={styles.pill}>{shown.kind}</span>
    </dd>
  </dl>
);

type ReviewBarProps = {
  readonly message: SupportMessageSummary;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onReview: () => void;
};

const ReviewBar = ({ message, busy, error, onReview }: ReviewBarProps): JSX.Element => (
  <div className={own.reviewBar}>
    {message.reviewedAt === null ? (
      <button type="button" className={styles.secondaryBtn} disabled={busy} onClick={onReview}>
        Mark reviewed
      </button>
    ) : (
      <span>
        Reviewed by {message.reviewedBy} · {formatRelativeTime(message.reviewedAt)}
      </span>
    )}
    {error !== null && <span role="alert">Could not mark it reviewed: {error}</span>}
  </div>
);
